import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const SettingsPage = () => {
    const savedColor = sessionStorage.getItem("navbarColor") || "#080235";

    const [activeTab, setActiveTab] = useState("appearance");
    const [navbarColor] = useState(savedColor);
    const [tempColor, setTempColor] = useState(savedColor);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [dropdownWidth, setDropdownWidth] = useState(0);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewCompanyInfo, setViewCompanyInfo] = useState(null);
    const navigate = useNavigate();


    // Company Info States
    const [companyInfoList, setCompanyInfoList] = useState([]);
    const [companyInfo, setCompanyInfo] = useState({
        id: null,
        companyName: "",
        logo: null,
        logoPreview: null,
        financialYear: "",
        startMonth: "",
        endMonth: "",
        startYear: "",
        email: "",
        contactNumber: "",
        website: "",
        address: "",
        taxId: "",
        description: "",
    });

    // Color options
    const colorOptions = [
        { name: "Default Blue", value: "#080235" },
        { name: "Black", value: "#000000" },
        { name: "Dark Gray", value: "#343a40" },
        { name: "Gray", value: "#6c757d" },
        { name: "Light Gray", value: "#adb5bd" },
        { name: "Green", value: "#198754" },
        { name: "Light Green", value: "#28a745" },
        { name: "Teal", value: "#20c997" },
        { name: "Cyan", value: "#17a2b8" },
        { name: "Sky Blue", value: "#87CEEB" },
        { name: "Blue", value: "#0d6efd" },
        { name: "Indigo", value: "#6610f2" },
        { name: "Purple", value: "#6f42c1" },
        { name: "Pink", value: "#d63384" },
        { name: "Red", value: "#dc3545" },
        { name: "Orange", value: "#fd7e14" },
        { name: "Yellow", value: "#ffc107" },
        { name: "Lime", value: "#cddc39" },
        { name: "Brown", value: "#795548" },
        { name: "Maroon", value: "#800000" },
        { name: "Olive", value: "#808000" },
        { name: "Navy", value: "#000080" },
        { name: "Magenta", value: "#ff00ff" },
        { name: "Violet", value: "#8a2be2" },
        { name: "Turquoise", value: "#40e0d0" },
        { name: "Coral", value: "#ff7f50" },
        { name: "Salmon", value: "#fa8072" },
        { name: "Gold", value: "#ffd700" },
        { name: "Silver", value: "#c0c0c0" },
        { name: "Bronze", value: "#cd7f32" },
        { name: "Sky Gray", value: "#b0c4de" },
        { name: "Mint", value: "#98ff98" },
        { name: "Peach", value: "#ffe5b4" },
        { name: "Cream", value: "#fffdd0" },
        { name: "Brick", value: "#b22222" },
        { name: "Persimmon", value: "#e5772a" },
        { name: "Slate Blue", value: "#607799" },
        { name: "Light Sky Blue", value: "#87cefa" },
    ];

     const token = sessionStorage.getItem("token");

    // 🔐 Authentication check
    useEffect(() => {
        const token = sessionStorage.getItem("token");
        const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");

        if (!token) {
            toast.error("You must be logged in to access this page.");
            navigate("/");
            return;
        }

        // ✅ check admin role
        if (!roles.includes("ROLE_ADMIN")) {
            toast.error("You are not authorized to access this page.");
            navigate("/");
            return;
        }
    }, [navigate]);

    // Fetch company info list
    useEffect(() => {
       
        if (activeTab === "company") {
            fetch(`${API_BASE_URL}/api/company-info`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`, // 👈 add token here
                },
            })
                .then((res) => {
                    if (!res.ok) {
                        throw new Error("Failed to fetch company info");
                    }
                    return res.json();
                })
                .then((data) => setCompanyInfoList(data))
                .catch((err) => console.error(err));
        }
    }, [activeTab, token]); // 👈 include token as dependency


    // Handle clicks outside color dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (dropdownRef.current) setDropdownWidth(dropdownRef.current.offsetWidth);
    }, [dropdownRef.current]);

    // Utility to build financial year
    const buildFinancialYear = (startMonth, endMonth, startYear) => {
        if (!startMonth || !endMonth || !startYear) return "";
        const endYear = ["January", "February", "March"].includes(endMonth)
            ? parseInt(startYear) + 1
            : parseInt(startYear);
        return `${startMonth} ${startYear} – ${endMonth} ${endYear}`;
    };

    // Handle form change
    const handleCompanyChange = (e) => {
        const { name, value } = e.target;
        setCompanyInfo({ ...companyInfo, [name]: value });
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCompanyInfo({
                ...companyInfo,
                logo: file,
                logoPreview: URL.createObjectURL(file),
            });
        }
    };

    // Save settings
    const handleSave = () => {
        if (activeTab === "appearance") {
            sessionStorage.setItem("navbarColor", tempColor);
            alert("Appearance preferences saved!");
            window.location.reload();
        } else if (activeTab === "company") {
            const formData = new FormData();
            formData.append(
                "companyInfo",
                new Blob([JSON.stringify(companyInfo)], { type: "application/json" })
            );
            if (companyInfo.logo) formData.append("logo", companyInfo.logo);

           fetch(`${API_BASE_URL}/api/company-info/save`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`, 
            },
            body: formData,
        })
                .then((res) => res.json())
                .then((data) => {
                    alert("Company information saved successfully!");
                    setCompanyInfoList((prev) => {
                        const index = prev.findIndex((c) => c.id === data.id);
                        if (index >= 0) {
                            prev[index] = data;
                            return [...prev];
                        } else return [...prev, data];
                    });
                    setCompanyInfo({
                        id: null,
                        companyName: "",
                        logo: null,
                        logoPreview: null,
                        financialYear: "",
                        startMonth: "",
                        endMonth: "",
                        startYear: "",
                        email: "",
                        contactNumber: "",
                        website: "",
                        address: "",
                        taxId: "",
                        description: "",
                    });
                })
                .catch((err) => {
                    console.error(err);
                    alert("Error saving company information");
                });
        }
    };

    const handleCancel = () => {
        setTempColor(savedColor);
        setDropdownOpen(false);
        setCompanyInfo({
            id: null,
            companyName: "",
            logo: null,
            logoPreview: null,
            financialYear: "",
            startMonth: "",
            endMonth: "",
            startYear: "",
            email: "",
            contactNumber: "",
            website: "",
            address: "",
            taxId: "",
            description: "",
            smtpPassword: "",
        });
        alert("Changes canceled.");
        navigate(-1);
    };

    // View/Edit/Delete
    const handleView = (info) => {
        setViewCompanyInfo(info);
        setViewModalOpen(true);
    };


    const handleEdit = (info) => {
        setCompanyInfo({
            ...info,
            logo: null,
            logoPreview: info.logoPath ? `${API_BASE_URL}/${info.logoPath}` : null,
        });
    };

    const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this company info?")) {
        fetch(`${API_BASE_URL}/api/company-info/${id}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`, 
            },
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error("Delete failed");
                }
                setCompanyInfoList((prev) => prev.filter((c) => c.id !== id));
                alert("Company info deleted successfully!");
            })
            .catch((err) => {
                console.error(err);
                alert("Error deleting company info");
            });
    }
};


    const handleColorSelect = (color) => {
        setTempColor(color);
        setDropdownOpen(false);
    };

    const tabs = [
        { id: "appearance", label: "Appearance" },
        { id: "company", label: "Company Information" },
        { id: "notifications", label: "Notifications" },
    ];

    return (
        <>
            {/* // <div className="d-flex flex-column vh-100 bg-light"> */}
            <div className="fixed-top">
                <AdminNavbar />
            </div>


            <div className="flex-grow-1 pt-5 p-4" style={{ marginTop: "80px" }}>

                <div className="mb-3">
                    <h2 className="fw-semibold">System Settings</h2>
                    <div className="mt-2">
                        <button className="btn btn-primary me-2" onClick={handleSave}>
                            Save
                        </button>
                        <button className="btn btn-secondary" onClick={handleCancel}>
                            Cancel
                        </button>
                    </div>
                </div>


                {/* Tabs */}
                <ul
                    className="nav nav-tabs mb-4"
                    style={{
                        backgroundColor: navbarColor,
                        borderRadius: "0",
                        padding: "6px 10px",
                    }}
                >
                    {tabs.map((tab) => (
                        <li className="nav-item" key={tab.id}>
                            <button
                                className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                                onClick={() => setActiveTab(tab.id)}
                                style={{
                                    color: activeTab === tab.id ? navbarColor : "#fff",
                                    backgroundColor: activeTab === tab.id ? "#fff" : "transparent",
                                    borderRadius: "6px",
                                    marginRight: "8px",
                                    fontWeight: "500",
                                }}
                            >
                                {tab.label}
                            </button>
                        </li>
                    ))}
                </ul>

                {/* ========== TAB CONTENT ========== */}
                {activeTab === "appearance" && (
                    <div className="card shadow-sm p-4 mb-4" style={{ maxWidth: "400px" }}>
                        <h5 className="mb-3 fw-semibold">Navbar Appearance</h5>
                        <div ref={dropdownRef} className="mb-3 position-relative">
                            <label className="form-label fw-bold">Select Navbar Color</label>
                            <div
                                className="form-control d-flex align-items-center justify-content-between"
                                style={{ cursor: "pointer" }}
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                            >
                                <div className="d-flex align-items-center">
                                    <span
                                        style={{
                                            display: "inline-block",
                                            width: "16px",
                                            height: "16px",
                                            borderRadius: "50%",
                                            backgroundColor: tempColor,
                                            marginRight: "8px",
                                        }}
                                    ></span>
                                    {colorOptions.find((c) => c.value === tempColor)?.name || "Select Color"}
                                </div>
                                <span>{dropdownOpen ? "▲" : "▼"}</span>
                            </div>

                            {dropdownOpen && (
                                <div
                                    className="position-absolute bg-white border shadow-sm"
                                    style={{
                                        width: `${dropdownWidth}px`,
                                        maxHeight: "200px",
                                        overflowY: "auto",
                                        zIndex: 1000,
                                    }}
                                >
                                    {colorOptions.map((color) => (
                                        <div
                                            key={color.value}
                                            className="d-flex align-items-center p-2 hover-bg"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleColorSelect(color.value)}
                                        >
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    width: "14px",
                                                    height: "14px",
                                                    borderRadius: "50%",
                                                    backgroundColor: color.value,
                                                    marginRight: "8px",
                                                }}
                                            ></span>
                                            {color.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "company" && (
                    <div className="card shadow-sm p-4 mb-5">
                        <h5 className="mb-4 fw-semibold">Company Information</h5>

                        {/* ======= Company Table ======= */}
                        <div className="mb-4">
                            <table className="table table-bordered table-striped">
                                <thead className="table-light">
                                    <tr>
                                        <th>Name</th>
                                        <th>Financial Year</th>
                                        <th>Email</th>
                                        <th>Contact</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companyInfoList && companyInfoList.length > 0 ? (
                                        companyInfoList.map((info) => (
                                            <tr key={info.id}>
                                                <td>{info.companyName}</td>
                                                <td>{info.financialYear}</td>
                                                <td>{info.email}</td>
                                                <td>{info.contactNumber}</td>
                                                <td>
                                                    <span
                                                        className="text-primary me-3 fw-semibold"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => handleView(info)}
                                                    >
                                                        View
                                                    </span>
                                                    <span
                                                        className="text-primary me-3 fw-semibold"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => handleEdit(info)}
                                                    >
                                                        Edit
                                                    </span>
                                                    <span
                                                        className="text-primary fw-semibold"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => handleDelete(info.id)}
                                                    >
                                                        Delete
                                                    </span>
                                                </td>

                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="text-center">
                                                No company info found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* ======= Company Form ======= */}
                        <div className="row g-4">
                            {/* Company Name */}
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Company Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="companyName"
                                    value={companyInfo.companyName}
                                    onChange={handleCompanyChange}
                                    placeholder="Enter company name"
                                />
                            </div>

                            {/* Financial Year */}
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Financial Year</label>
                                <div className="d-flex align-items-center flex-wrap gap-2">
                                    <select
                                        className="form-select"
                                        style={{ width: "auto", minWidth: "120px" }}
                                        value={companyInfo.startMonth || ""}
                                        onChange={(e) =>
                                            setCompanyInfo({
                                                ...companyInfo,
                                                startMonth: e.target.value,
                                                financialYear: buildFinancialYear(
                                                    e.target.value,
                                                    companyInfo.endMonth,
                                                    companyInfo.startYear
                                                ),
                                            })
                                        }
                                    >
                                        <option value="">Start Month</option>
                                        {[
                                            "January", "February", "March", "April", "May", "June",
                                            "July", "August", "September", "October", "November", "December"
                                        ].map((month) => (
                                            <option key={month} value={month}>{month}</option>
                                        ))}
                                    </select>

                                    <span className="fw-semibold">to</span>

                                    <select
                                        className="form-select"
                                        style={{ width: "auto", minWidth: "120px" }}
                                        value={companyInfo.endMonth || ""}
                                        onChange={(e) =>
                                            setCompanyInfo({
                                                ...companyInfo,
                                                endMonth: e.target.value,
                                                financialYear: buildFinancialYear(
                                                    companyInfo.startMonth,
                                                    e.target.value,
                                                    companyInfo.startYear
                                                ),
                                            })
                                        }
                                    >
                                        <option value="">End Month</option>
                                        {[
                                            "January", "February", "March", "April", "May", "June",
                                            "July", "August", "September", "October", "November", "December"
                                        ].map((month) => (
                                            <option key={month} value={month}>{month}</option>
                                        ))}
                                    </select>

                                    <select
                                        className="form-select"
                                        style={{ width: "auto", minWidth: "100px" }}
                                        value={companyInfo.startYear || ""}
                                        onChange={(e) =>
                                            setCompanyInfo({
                                                ...companyInfo,
                                                startYear: e.target.value,
                                                financialYear: buildFinancialYear(
                                                    companyInfo.startMonth,
                                                    companyInfo.endMonth,
                                                    e.target.value
                                                ),
                                            })
                                        }
                                    >
                                        <option value="">Start Year</option>
                                        {Array.from({ length: 6 }).map((_, i) => {
                                            const year = new Date().getFullYear() - 2 + i;
                                            return (
                                                <option key={year} value={year}>{year}</option>
                                            );
                                        })}
                                    </select>
                                </div>

                                {companyInfo.financialYear && (
                                    <small className="text-muted mt-2 d-block">
                                        Selected Financial Year: <strong>{companyInfo.financialYear}</strong>
                                    </small>
                                )}
                            </div>

                            {/* Email */}
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Company Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    value={companyInfo.email}
                                    onChange={handleCompanyChange}
                                    placeholder="Enter official email"
                                />
                            </div>

                            {/* Contact */}
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Contact Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="contactNumber"
                                    value={companyInfo.contactNumber}
                                    onChange={handleCompanyChange}
                                    placeholder="Enter contact number"
                                />
                            </div>

                            {/* Website */}
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Website</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="website"
                                    value={companyInfo.website}
                                    onChange={handleCompanyChange}
                                    placeholder="https://example.com"
                                />
                            </div>

                            {/* Tax */}
                            <div className="col-md-4">
                                <label className="form-label fw-bold">GST / Tax ID</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="taxId"
                                    value={companyInfo.taxId}
                                    onChange={handleCompanyChange}
                                    placeholder="Enter GST or Tax ID"
                                />
                            </div>

                            {/* App password */}
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Mail App password</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="smtpPassword"
                                    value={companyInfo.smtpPassword}
                                    onChange={handleCompanyChange}
                                    placeholder="Enter Mail App password"
                                />
                            </div>

                            {/* Address */}
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Company Address</label>
                                <textarea
                                    className="form-control"
                                    name="address"
                                    rows="2"
                                    value={companyInfo.address}
                                    onChange={handleCompanyChange}
                                    placeholder="Enter company address"
                                ></textarea>
                            </div>

                            {/* Description */}
                            <div className="col-md-6">
                                <label className="form-label fw-bold">About Company</label>
                                <textarea
                                    className="form-control"
                                    name="description"
                                    rows="3"
                                    value={companyInfo.description}
                                    onChange={handleCompanyChange}
                                    placeholder="Brief description about the company"
                                ></textarea>
                            </div>

                            {/* Logo */}
                            <div className="col-md-6">
                                <label className="form-label fw-bold">Upload Company Logo</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="form-control"
                                    onChange={handleLogoUpload}
                                />
                                {companyInfo.logoPreview && (
                                    <div className="mt-3 text-center">
                                        <img
                                            src={companyInfo.logoPreview}
                                            alt="Logo Preview"
                                            style={{
                                                width: "120px",
                                                height: "120px",
                                                objectFit: "contain",
                                                borderRadius: "8px",
                                                border: "1px solid #ccc",
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="mt-2">
                    <button className="btn btn-primary me-2" onClick={handleSave}>
                        Save
                    </button>
                    <button className="btn btn-secondary" onClick={handleCancel}>
                        Cancel
                    </button>
                </div>
            </div>

            {/* ===== View Company Modal ===== */}
            {viewModalOpen && viewCompanyInfo && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h5 className="mb-3">{viewCompanyInfo.companyName}</h5>
                        {viewCompanyInfo.logoPath && (
                            <img
                                src={`${API_BASE_URL}/${viewCompanyInfo.logoPath}`}
                                alt="Company Logo"
                                style={{ width: "150px", height: "150px", objectFit: "contain", marginBottom: "15px", borderRadius: "8px" }}
                            />
                        )}
                        <p><strong>Financial Year:</strong> {viewCompanyInfo.financialYear}</p>
                        <p><strong>Email:</strong> {viewCompanyInfo.email}</p>
                        <p><strong>Contact:</strong> {viewCompanyInfo.contactNumber}</p>
                        <p><strong>Website:</strong> {viewCompanyInfo.website}</p>
                        <p><strong>GST / Tax ID:</strong> {viewCompanyInfo.taxId}</p>
                        <p><strong>Mail App password:</strong> {viewCompanyInfo.smtpPassword}</p>
                        <p><strong>Address:</strong> {viewCompanyInfo.address}</p>
                        <p><strong>About:</strong> {viewCompanyInfo.description}</p>
                        <div className="text-end">
                            <button className="btn btn-secondary" onClick={() => setViewModalOpen(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}


            <style>{`
        .hover-bg:hover { background-color: #f1f1f1; }
        .form-label { font-size: 0.9rem; }
        .card { border-radius: 12px; }
        .nav-tabs { border: none; }
        .nav-tabs .nav-link.active { font-weight: 600; box-shadow: 0px 2px 4px rgba(0,0,0,0.1); }

        .modal-backdrop {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background-color: rgba(0,0,0,0.5);
    display: flex; justify-content: center; align-items: center;
    z-index: 2000;
  }
  .modal-content {
    background: #fff;
    padding: 25px;
    border-radius: 12px;
    max-width: 500px;
    width: 90%;
    box-shadow: 0 4px 15px rgba(0,0,0,0.3);
  }
  .modal-content img { display: block; margin: 0 auto; }
      `}</style>
        </>

    );
};

export default SettingsPage;
